/**
 * alwaysIn — Brooks "always-in" direction classifier for a single timeframe.
 *
 * Per Al Brooks's price-action methodology, "always-in" is the answer to
 * "if I were FORCED to be long or short RIGHT NOW, which way?" It's a
 * binary state per bar — long, short, or unclear when neither side has the
 * edge. Used by the Top-Down per-TF state vector (Top-Down §2.4) and as
 * an HTF context check ("don't fight always-in on the HTF").
 *
 * Heuristic (from Top-Down design doc §2.4):
 *   - Long if last 3 bars closing > opening AND closes are above 21 EMA.
 *   - Short if last 3 bars closing < opening AND closes are below 21 EMA.
 *   - Long if the most recent strong-bodied bar (body/range ≥ 0.65) closes
 *     above 21 EMA in up direction.
 *   - Short if the most recent strong-bodied bar closes below 21 EMA down.
 *   - Otherwise unclear.
 *
 * Pure function. v1: refine if false-positive flips appear in logged data
 * (Top-Down §9.2).
 */

export const DEFAULT_OPTS = Object.freeze({
  lookback3BarBars: 3,
  strongBodyRatio: 0.65,
});

/**
 * Classify always-in direction at the end of `bars`.
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number}>} bars
 * @param {Object} ctx
 *   - ema21: Array<{time, value}>  Same-length-as-bars EMA-21 series.
 * @param {Object} [opts]
 * @returns {'long' | 'short' | 'unclear'}
 */
export const detectAlwaysInAt = (bars, ctx = {}, opts = {}) => {
  if (!Array.isArray(bars) || bars.length < 3) return 'unclear';
  const ema21 = ctx.ema21;
  if (!Array.isArray(ema21) || ema21.length !== bars.length) return 'unclear';

  const lookback = Math.max(2, Math.floor(Number(opts.lookback3BarBars ?? DEFAULT_OPTS.lookback3BarBars)));
  const strongRatio = Number(opts.strongBodyRatio ?? DEFAULT_OPTS.strongBodyRatio);

  const endIdx = bars.length - 1;
  const start = endIdx - lookback + 1;
  if (start < 0) return 'unclear';

  // Pattern 1: last `lookback` bars all closing > opening AND all closes above EMA21.
  let allUp = true, allDown = true, allAboveEma = true, allBelowEma = true;
  for (let i = start; i <= endIdx; i++) {
    const o = Number(bars[i]?.open);
    const c = Number(bars[i]?.close);
    const e = Number(ema21[i]?.value);
    if (!Number.isFinite(o) || !Number.isFinite(c) || !Number.isFinite(e)) {
      allUp = false; allDown = false; allAboveEma = false; allBelowEma = false;
      break;
    }
    if (c <= o) allUp = false;
    if (c >= o) allDown = false;
    if (c <= e) allAboveEma = false;
    if (c >= e) allBelowEma = false;
  }
  if (allUp && allAboveEma) return 'long';
  if (allDown && allBelowEma) return 'short';

  // Pattern 2: most recent strong-bodied bar's direction + EMA position.
  // Scan backwards for the most recent bar where |body|/range >= strongRatio.
  for (let i = endIdx; i >= start; i--) {
    const o = Number(bars[i]?.open);
    const h = Number(bars[i]?.high);
    const l = Number(bars[i]?.low);
    const c = Number(bars[i]?.close);
    const e = Number(ema21[i]?.value);
    if (![o, h, l, c, e].every(Number.isFinite)) continue;
    const range = h - l;
    if (range <= 0) continue;
    const body = Math.abs(c - o);
    const ratio = body / range;
    if (ratio < strongRatio) continue;
    // Found a strong bar.
    if (c > o && c > e) return 'long';
    if (c < o && c < e) return 'short';
    break;
  }

  return 'unclear';
};
